import { XIcon } from 'lucide-react';
import { FC, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { KM_DOMAINS, KM_ENTITY_TYPES, useKmEntities, type KmEntity } from '@/entities/ontology';
import { useHasPermission } from '@/entities/profile';
import { IconButton } from '@/shared/ui';
import { KmNoteSetPicker } from './KmNoteSetPicker';
import type { KmAssessmentFormValues } from './types';

type KmEvalNoteSetsProps = {
  index: number;
};

/** Сколько наборов нод знаний предлагать на выбор */
const NOTE_SETS_LIMIT = 200;

/** Название набора берётся из значений его атрибутов; безымянный набор называем его номером */
const getNoteSetName = (entity: KmEntity) =>
  typeof entity.data.name === 'string' && entity.data.name.trim() ? entity.data.name : `#${entity.id}`;

/**
 * Наборы нод знаний определения оценки: на их ноды опирается модель, когда оценивает
 * единицу. Наборы живут в модели знаний (домен знаний), а не в прежней базе знаний —
 * прежние сеты знаний этому режиму не подходят. Состав правится в форме и уходит на
 * сервер при сохранении карточки.
 */
export const KmEvalNoteSets: FC<KmEvalNoteSetsProps> = ({ index }) => {
  const { t } = useTranslation('expertReport');
  const allowAdmin = useHasPermission('ADMIN_EXPERT_REPORT');
  const { getValues, setValue, watch } = useFormContext<KmAssessmentFormValues>();

  // Перечень строк списка определений и значения формы расходятся на кадр: при переходе к другой
  // единице оценивания строка прошлой ещё отрисована, а состава по её номеру в форме уже нет
  const noteSets = watch(`evals.${index}.noteSets`) ?? [];

  const { data: noteSetsPage } = useKmEntities({
    type: [KM_ENTITY_TYPES.NOTE_SET],
    domain: [KM_DOMAINS.KNOWLEDGE],
    limit: NOTE_SETS_LIMIT,
  });

  const linkedIds = useMemo(() => new Set(noteSets.map((item) => item.id)), [noteSets]);

  const comboboxOptions = useMemo(
    () =>
      (noteSetsPage?.items ?? [])
        .filter((item) => !linkedIds.has(item.id))
        .map((item) => ({ value: item.id, label: getNoteSetName(item) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [noteSetsPage, linkedIds],
  );

  const handleRemove = useCallback(
    (noteSetId: number) => {
      const current = getValues(`evals.${index}.noteSets`) ?? [];
      setValue(
        `evals.${index}.noteSets`,
        current.filter((item) => item.id !== noteSetId),
        { shouldDirty: true },
      );
    },
    [index, getValues, setValue],
  );

  const handleAdd = useCallback(
    (value: unknown) => {
      const id = Number(value);
      const item = noteSetsPage?.items.find((option) => option.id === id);
      if (!item) return;

      const current = getValues(`evals.${index}.noteSets`) ?? [];
      setValue(`evals.${index}.noteSets`, [...current, { id: item.id, name: getNoteSetName(item) }], {
        shouldDirty: true,
      });
    },
    [index, getValues, setValue, noteSetsPage],
  );

  const loadedCount = noteSetsPage?.items.length ?? 0;
  const total = noteSetsPage?.total;

  if (noteSets.length === 0) {
    // Без права на редактирование и без привязок показывать нечего
    if (!allowAdmin) return null;
    return (
      <KmNoteSetPicker
        options={comboboxOptions}
        loadedCount={loadedCount}
        total={total}
        placeholder={t('km.linkNoteSet')}
        onSelect={handleAdd}
      />
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-muted-foreground">{t('km.noteSets')}</div>

      <div className="flex flex-col gap-1">
        {noteSets.map((noteSet) => (
          <div key={noteSet.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm">
            <span className="flex-1 truncate">{noteSet.name}</span>
            {allowAdmin && (
              <IconButton
                size="icon"
                label={t('km.removeNoteSet')}
                icon={<XIcon className="size-3.5" />}
                onClick={() => handleRemove(noteSet.id)}
              />
            )}
          </div>
        ))}
      </div>

      {allowAdmin && comboboxOptions.length > 0 && (
        <KmNoteSetPicker
          options={comboboxOptions}
          loadedCount={loadedCount}
          total={total}
          placeholder={t('km.addNoteSet')}
          onSelect={handleAdd}
        />
      )}
    </div>
  );
};
